import { PrintSettings, ReceiptData } from "../types/print-setting";
import { initAllStores } from "../utils/cache";
import { DEFAULT_PRINT_SETTINGS } from "../constants";
import Hotel from "../models/Hotel";
import Logo from "../assets/hotel_logo.png";

const fontSizeMap = { small: "text-sm", medium: "text-base", large: "text-lg" };

// Ensures all text prints sharp black even when converted to PDF
const printStyle = `
@media print {
  * {
    color: #000 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
`;
export function ReceiptBody({ settings, sample }: { settings: PrintSettings; sample: ReceiptData }) {
  const fontSizeClass = fontSizeMap[settings.fontSize];

  // Format date/time nicely
  const issuedAt =
    new Date().toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

  return (
    <>
      <style>{`
      @media print {
        * {
          color: #000 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        body {
          margin: 0;
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
          zoom: 100%;
        }

       @page {
          size: A4 portrait;
          margin: 5mm;
        } 

        .receipt {
          width: 100%;
          max-width: 190mm; /* fits perfectly on A4 */
          margin: auto;
          page-break-inside: avoid;
          break-inside: avoid-page;
          transform: scale(1);
        }
      }

      .receipt * {
        color: #000;
      }
    `}</style>

      <div className={`receipt p-2.5 text-[14px] leading-snug text-black ${fontSizeClass}`}>
        {/* ===== Header: Logo centered, name bigger ===== */}
        <div className="text-center mb-2">
          {settings.showLogo && settings.receiptLogoUrl && (
            <img
              src={Logo}
              alt="Business Logo"
              className="h-14 mx-auto mb-1 object-contain"
            />
          )}
          <div className="font-bold text-xl tracking-wide">{settings.displayName}</div>
        </div>

        {/* ===== Contact Info ===== */}
        {settings.includeAddress && (
          <div className="text-[12px] font-medium mb-2 leading-tight text-center">
            <div>{sample.address}</div>
            <div className="flex justify-center flex-wrap gap-x-2 mt-1">
              {sample.phone && <span>📞 {sample.phone}</span>}
              {sample.email && <span>✉️ {sample.email}</span>}
              {sample.website && <span>🌐 {sample.website}</span>}
            </div>
          </div>
        )}

        {/* ===== Guest & Date ===== */}
        <div className="border-b border-black pb-1 mb-2 text-[14px] font-medium">
          <div className="flex justify-between">
            <span>Guest:</span>
            <span>{sample.guest}</span>
          </div>
          <div className="flex justify-between text-[12px] text-gray-700 mt-1">
            <span>Date:</span>
            <span>{issuedAt}</span>
          </div>
        </div>

        {/* ===== Items ===== */}
        <div className="mb-2">
          {sample.items.map((it, idx) => (
            <div key={idx} className="flex justify-between text-[14px]">
              <span>{it.desc}</span>
              <span>₦{it.amount}</span>
            </div>
          ))}
        </div>

        {/* ===== Totals ===== */}
        {sample.discount && sample.discount !== "0" && (
          <div className="flex justify-between text-[14px] mb-1">
            <span>Discount</span>
            <span>₦{sample.discount}</span>
          </div>
        )}

        <div className="flex justify-between font-semibold text-black border-t border-black pt-1 mb-1 text-[15px]">
          <span>Total Paid</span>
          <span>₦{sample.total}</span>
        </div>

        {/* ===== Amount in Words ===== */}
        {sample.amountInWords && (
          <div className="text-[14px] italic border-t border-gray-400 pt-1 mb-2">
            Amount in Words: <span className="font-semibold">{sample.amountInWords}</span>
          </div>
        )}

        {/* ===== Signature Section ===== */}
        {settings.showSignature ? (
          <div className="mt-6 text-[13px] flex justify-between items-end">
            {/* Staff */}
            <div className="w-1/2">
              <div className="border-t border-black w-36 mb-1"></div>
              <div className="font-semibold">{"Staff: " + sample.issuedBy || "Staff Signature"}</div>
            </div>

            {/* Customer */}
            <div className="w-1/2 text-right">
              <div className="border-t border-black w-36 ml-auto mb-1"></div>
              <div className="font-semibold">Customer Signature</div>
            </div>
          </div>
        ) : (
          <div className="text-[13px] mt-1">Issued By: {sample.issuedBy || "_______________"}</div>
        )}

        {/* ===== Footer Note ===== */}
        {settings.showFooterNote && (
          <div className="mt-2 text-center text-[13px] border-t border-gray-200 pt-1 font-medium">
            {settings.footerNote}
          </div>
        )}
      </div>

    </>
  );

}

export function A4Preview({
  settings,
  sample,
}: {
  settings: PrintSettings;
  sample: ReceiptData;
}) {
  const hasDuplicate = Boolean(settings.includeDuplicate);

  return (
    <div className="w-full p-0 print:p-0 flex flex-col items-center gap-4 overflow-x-auto">
      {/* Customer Copy */}
      <div
        className="bg-white p-3 w-full max-w-[190mm] text-black"
        style={{
          border: "1px solid #000", // border for cutting
          borderRadius: "4px",
        }}
      >
        {settings.showCustomerCopyLabel && (
          <div className="text-xs font-medium text-center mb-1">
            {settings.customerCopyLabel}
          </div>
        )}
        <ReceiptBody settings={settings} sample={sample} />
      </div>

      {hasDuplicate && (
        <div
          className="bg-white p-3 mt-4 w-full max-w-[190mm] text-black"
          style={{
            border: "1px solid #000",
            borderRadius: "4px",
          }}
        >
          {settings.showBusinessCopyLabel && (
            <div className="text-xs font-medium text-center mb-1">
              {settings.businessCopyLabel}
            </div>
          )}
          <ReceiptBody settings={settings} sample={sample} />
        </div>
      )}

      <style>{`
        @media print {
          .receipt-body {
            page-break-inside: avoid;
          }
          @page {
            size: A4 portrait;
            margin: 5mm;
          }
        }
      `}</style>
    </div>
  );
}


export function ThermalPreview({
  settings,
  sample,
}: {
  settings: PrintSettings;
  sample: ReceiptData;
}) {
  const px = settings.paperWidth === "58mm" ? 220 : 320;
  const hasDuplicate = Boolean(settings.includeDuplicate);

  return (
    <div className="w-full p-0 flex justify-center">
      <div
        className="border border-gray-400 rounded-lg bg-white p-4 text-black"
        style={{ width: px, minHeight: 400 }}
      >
        {settings.showCustomerCopyLabel && (
          <div className="text-xs font-medium text-center mb-1">
            {settings.customerCopyLabel}
          </div>
        )}
        <ReceiptBody settings={settings} sample={sample} />

        {hasDuplicate && (
          <>
            <div className="border-t border-dashed my-2" />
            {settings.showBusinessCopyLabel && (
              <div className="text-xs font-medium text-center mb-1">
                {settings.businessCopyLabel}
              </div>
            )}
            <ReceiptBody settings={settings} sample={sample} />
          </>
        )}
      </div>
    </div>
  );
}

export function getReceiptPreview({
  settings,
  sample,
}: {
  settings: PrintSettings;
  sample: ReceiptData;
}) {
  switch (settings.printerType) {
    case "A4":
      return <A4Preview settings={settings} sample={sample} />;
    case "THERMAL_58":
      return <ThermalPreview settings={settings} sample={sample} />;
    case "THERMAL_80":
      return <ThermalPreview settings={settings} sample={sample} />;
    default:
      return <A4Preview settings={settings} sample={sample} />;
  }
}

export async function getHotelReceipt(sample: ReceiptData) {
  const cache = await initAllStores();
  const hotel: Hotel | null = await cache.getBy("data", { id: "hotel" });
  if (!hotel) return null;

  const printSettings: PrintSettings = {
    ...DEFAULT_PRINT_SETTINGS,
    ...(hotel.print || {}),
    receiptLogoUrl: hotel?.imagePath ?? "",
  };


  return getReceiptPreview({ settings: printSettings, sample });
}
